<template>
  <div class="notas-locais">
    <!-- Barra superior -->
    <div class="notas-header">
      <div class="notas-header__left">
        <h1 class="notas-header__title">Notas Fiscais Locais</h1>
        <span class="notas-header__count" v-if="!carregando">
          {{ notasFiltradas.length }} registro(s)
        </span>
      </div>
      <div class="notas-header__right">
        <button class="btn btn-sm btn-outline-secondary mr-2" @click="carregar" title="Recarregar lista">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="btn btn-primary" @click="$router.push({ name: 'pdvDesktopNovaVenda' })">
          <i class="fas fa-plus-circle mr-1"></i> Nova Venda
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="notas-filtros">
      <input
        v-model="filtro.busca"
        type="text"
        class="form-control notas-filtros__busca"
        placeholder="Buscar por número, cliente ou chave..."
      />
      <select v-model="filtro.statusFiscal" class="form-control notas-filtros__status">
        <option value="">Todos (fiscal)</option>
        <option value="pendente_xml">Pendente XML</option>
        <option value="xml_gerado">XML Gerado</option>
        <option value="transmitida">Transmitida</option>
        <option value="autorizada">Autorizada</option>
        <option value="rejeitada">Rejeitada</option>
      </select>
      <select v-model="filtro.statusSync" class="form-control notas-filtros__status">
        <option value="">Todos (sync)</option>
        <option value="pendente">Pendente Sync</option>
        <option value="sincronizada">Sincronizada</option>
        <option value="erro">Erro Sync</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="carregando" class="notas-loading">
      <span class="spinner-border spinner-border-sm mr-2"></span>
      Carregando...
    </div>

    <!-- Tabela -->
    <div v-else-if="notasFiltradas.length > 0" class="notas-table-wrapper">
      <table class="table table-hover notas-table">
        <thead>
          <tr>
            <th>N°/Nota</th>
            <th>Emissão</th>
            <th>Cliente</th>
            <th>Sér.</th>
            <th>Chave de Acesso</th>
            <th class="text-right">Total</th>
            <th class="text-center">Fiscal</th>
            <th class="text-center">Sync</th>
            <th class="text-center">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="nota in notasFiltradas" :key="nota.uuid_venda_terminal || nota.id">
            <td class="font-weight-bold">
              {{ nota.numeroNota || nota.id }}
            </td>
            <td>{{ formatarData(nota.emissao || nota.dtVenda || nota.created_at) }}</td>
            <td>{{ nota.nomeCliente || '—' }}</td>
            <td>{{ nota.serie || '—' }}</td>
            <td class="notas-table__chave" :title="nota.chave">
              {{ nota.chave ? nota.chave.substring(0, 22) + '...' : '—' }}
            </td>
            <td class="text-right font-weight-bold">
              {{ formatarValor(nota.valorTotal) }}
            </td>
            <td class="text-center">
              <span :class="badgeFiscal(nota.status_fiscal)" class="badge">
                {{ labelFiscal(nota.status_fiscal) }}
              </span>
              <div
                v-if="gerandoXml[nota.uuid_venda_terminal]"
                class="notas-xml-spinner"
              >
                <span class="spinner-border spinner-border-sm"></span>
              </div>
              <div
                v-if="erroXml[nota.uuid_venda_terminal]"
                class="notas-xml-erro"
                :title="erroXml[nota.uuid_venda_terminal]"
              >
                <i class="fas fa-exclamation-circle text-danger"></i>
                <small class="text-danger">Erro</small>
              </div>
            </td>
            <td class="text-center">
              <span
                :class="[
                  'badge',
                  nota.status_sync === 'sincronizada' ? 'badge-success' :
                  nota.status_sync === 'erro'         ? 'badge-danger'  : 'badge-warning'
                ]"
              >
                {{
                  nota.status_sync === 'sincronizada' ? '✓ Sync' :
                  nota.status_sync === 'erro'         ? 'Erro'   : 'Pendente'
                }}
              </span>
            </td>
            <td class="text-center notas-table__acoes">
              <!-- Gerar XML -->
              <button
                v-if="nota.status_fiscal === 'pendente_xml'"
                class="btn btn-sm btn-warning mr-1"
                title="Gerar XML NFC-e"
                :disabled="!!gerandoXml[nota.uuid_venda_terminal]"
                @click="gerarXml(nota)"
              >
                <span v-if="gerandoXml[nota.uuid_venda_terminal]">
                  <span class="spinner-border spinner-border-sm"></span>
                </span>
                <span v-else>
                  <i class="fas fa-file-code"></i>
                </span>
              </button>
              <!-- Re-gerar XML (quando já gerado mas quer rever) -->
              <button
                v-else-if="['xml_gerado','rejeitada'].includes(nota.status_fiscal)"
                class="btn btn-sm btn-outline-warning mr-1"
                title="Re-gerar XML NFC-e"
                :disabled="!!gerandoXml[nota.uuid_venda_terminal]"
                @click="gerarXml(nota)"
              >
                <i class="fas fa-redo"></i>
              </button>

              <!-- Ver XML -->
              <button
                v-if="nota.xml"
                class="btn btn-sm btn-light-info mr-1"
                title="Visualizar / copiar XML"
                @click="verXml(nota)"
              >
                <i class="fas fa-code"></i>
              </button>

              <!-- Ver erro geração -->
              <button
                v-if="erroXml[nota.uuid_venda_terminal]"
                class="btn btn-sm btn-outline-danger mr-1"
                title="Ver erro da geração"
                @click="verErroXml(nota)"
              >
                <i class="fas fa-exclamation-triangle"></i>
              </button>

              <!-- DANFE NFC-e -->
              <button
                v-if="nota.xml_assinado || nota.status_fiscal === 'autorizada'"
                class="btn btn-sm btn-success mr-1"
                title="Visualizar / Imprimir DANFE NFC-e"
                @click="modalDanfe = { visivel: true, uuid: nota.uuid_venda_terminal }"
              >
                <i class="fas fa-receipt"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Vazio -->
    <div v-else class="notas-vazio">
      <i class="fas fa-file-invoice fa-3x text-muted mb-3"></i>
      <p class="text-muted">Nenhuma nota encontrada.</p>
      <button class="btn btn-primary" @click="$router.push({ name: 'pdvDesktopNovaVenda' })">
        Nova Venda
      </button>
    </div>

    <!-- Modal XML -->
    <div v-if="modalXml.visivel" class="modal-overlay" @click.self="modalXml.visivel = false">
      <div class="modal-xml">
        <div class="modal-xml__header">
          <div>
            <h4>XML NFC-e — Nota #{{ modalXml.nota && (modalXml.nota.numeroNota || modalXml.nota.numero) }}</h4>
            <small class="text-muted" v-if="modalXml.nota && modalXml.nota.chave">
              Chave: {{ modalXml.nota.chave }}
            </small>
          </div>
          <button class="btn btn-sm btn-light" @click="modalXml.visivel = false">✕</button>
        </div>
        <pre class="modal-xml__content">{{ modalXml.conteudo }}</pre>
        <div class="modal-xml__footer">
          <span v-if="copiado" class="text-success mr-3 font-weight-bold">
            <i class="fas fa-check mr-1"></i>Copiado!
          </span>
          <button class="btn btn-sm btn-outline-secondary" @click="copiarXml">
            <i class="fas fa-copy mr-1"></i>Copiar XML
          </button>
        </div>
      </div>
    </div>

    <!-- Modal erro de geração -->
    <div v-if="modalErro.visivel" class="modal-overlay" @click.self="modalErro.visivel = false">
      <div class="modal-confirm">
        <h4 class="modal-confirm__title text-danger">
          <i class="fas fa-exclamation-circle mr-2"></i>Erro na Geração do XML
        </h4>
        <p class="text-muted mb-1">
          Nota #{{ modalErro.nota && (modalErro.nota.numeroNota || modalErro.nota.numero) }}
        </p>
        <div class="alert alert-danger" style="font-size:0.85rem; word-break:break-word;">
          {{ modalErro.mensagem }}
        </div>
        <div class="modal-confirm__actions">
          <button class="btn btn-secondary mr-2" @click="modalErro.visivel = false">Fechar</button>
          <button class="btn btn-warning" @click="gerarXml(modalErro.nota); modalErro.visivel = false">
            <i class="fas fa-redo mr-1"></i>Tentar Novamente
          </button>
        </div>
      </div>
    </div>

    <!-- Modal DANFE NFC-e -->
    <DanfeNfce
      :visivel="modalDanfe.visivel"
      :uuid="modalDanfe.uuid"
      @fechar="modalDanfe.visivel = false"
    />
  </div>
</template>

<script>
// XML gerado no processo principal via IPC (NfceXmlBuilderService)
import DanfeNfce from "../components/DanfeNfce.vue";

export default {
  name: "PdvNotasLocais",

  components: { DanfeNfce },

  data() {
    return {
      notas: [],
      carregando: true,
      filtro: {
        busca: "",
        statusFiscal: "",
        statusSync: "",
      },
      // uuid_venda_terminal → true enquanto gera
      gerandoXml: {},
      // uuid_venda_terminal → mensagem de erro
      erroXml: {},
      copiado: false,
      modalXml: {
        visivel: false,
        nota: null,
        conteudo: "",
      },
      modalErro: {
        visivel: false,
        nota: null,
        mensagem: "",
      },
      // Modal DANFE NFC-e
      modalDanfe: {
        visivel: false,
        uuid:    null,
      },
    };
  },

  computed: {
    notasFiltradas() {
      return this.notas.filter((n) => {
        const busca = (this.filtro.busca || "").toLowerCase();
        const matchBusca =
          !busca ||
          String(n.numeroNota || n.id).toLowerCase().includes(busca) ||
          (n.nomeCliente || "").toLowerCase().includes(busca) ||
          (n.chave || "").includes(busca);

        const matchFiscal =
          !this.filtro.statusFiscal ||
          n.status_fiscal === this.filtro.statusFiscal;

        const matchSync =
          !this.filtro.statusSync ||
          n.status_sync === this.filtro.statusSync;

        return matchBusca && matchFiscal && matchSync;
      });
    },
  },

  async mounted() {
    await this.carregar();
  },

  methods: {
    async carregar() {
      this.carregando = true;
      try {
        this.notas = (await this.$electron.nfce.getAll()) || [];
      } catch (err) {
        this.$toast.error("Erro ao carregar notas: " + (err.message || err));
      } finally {
        this.carregando = false;
      }
    },

    // ── Gerar XML ──────────────────────────────────────────────────────────

    async gerarXml(nota) {
      const uuid = nota.uuid_venda_terminal;
      if (!uuid) {
        this.$toast.error("Nota sem UUID — não é possível gerar XML.");
        return;
      }

      this.$set(this.gerandoXml, uuid, true);
      this.$set(this.erroXml,    uuid, null);

      try {
        const resultado = await this.$electron.nfce.gerarXml(uuid);
        this.$toast.success("XML gerado com sucesso!");

        // Atualiza a nota localmente sem recarregar tudo
        const idx = this.notas.findIndex(
          (n) => n.uuid_venda_terminal === uuid
        );
        if (idx !== -1) {
          this.$set(this.notas, idx, {
            ...this.notas[idx],
            status_fiscal: "xml_gerado",
            chave: resultado.chave,
            xml: resultado.xml,
          });
        } else {
          await this.carregar();
        }
      } catch (err) {
        const msg = err.message || String(err);
        this.$set(this.erroXml, uuid, msg);
        this.$toast.error("Falha ao gerar XML: " + msg);
      } finally {
        this.$set(this.gerandoXml, uuid, false);
      }
    },

    // ── Visualizar XML ─────────────────────────────────────────────────────

    verXml(nota) {
      const conteudo = nota.xml || "";
      this.modalXml.nota     = nota;
      this.modalXml.conteudo = conteudo || "(sem XML gerado)";
      this.modalXml.visivel  = true;
      this.copiado           = false;
    },

    copiarXml() {
      navigator.clipboard
        .writeText(this.modalXml.conteudo)
        .then(() => {
          this.copiado = true;
          setTimeout(() => { this.copiado = false; }, 2500);
        })
        .catch(() => this.$toast.error("Erro ao copiar para a área de transferência."));
    },

    // ── Modal de erro ──────────────────────────────────────────────────────

    verErroXml(nota) {
      this.modalErro.nota     = nota;
      this.modalErro.mensagem = this.erroXml[nota.uuid_venda_terminal] || "Erro desconhecido.";
      this.modalErro.visivel  = true;
    },

    // ── Formatadores ───────────────────────────────────────────────────────

    formatarData(dt) {
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day:    "2-digit",
          month:  "2-digit",
          year:   "numeric",
          hour:   "2-digit",
          minute: "2-digit",
        });
      } catch {
        return String(dt);
      }
    },

    formatarValor(v) {
      if (v == null) return "—";
      return Number(v).toLocaleString("pt-BR", {
        style:    "currency",
        currency: "BRL",
      });
    },

    // ── Badges ─────────────────────────────────────────────────────────────

    badgeFiscal(status) {
      const map = {
        pendente_xml: "badge-warning",
        xml_gerado:   "badge-info",
        transmitida:  "badge-primary",
        autorizada:   "badge-success",
        rejeitada:    "badge-danger",
        cancelada:    "badge-secondary",
      };
      return map[status] || "badge-light";
    },

    labelFiscal(status) {
      const map = {
        pendente_xml: "Pend. XML",
        xml_gerado:   "XML Gerado",
        transmitida:  "Transmitida",
        autorizada:   "Autorizada",
        rejeitada:    "Rejeitada",
        cancelada:    "Cancelada",
      };
      return map[status] || (status || "—");
    },
  },
};
</script>

<style scoped>
.notas-locais {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.notas-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.notas-header__title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}
.notas-header__count {
  font-size: 0.85rem;
  color: #6c757d;
  margin-left: 0.75rem;
}

/* Filtros */
.notas-filtros {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}
.notas-filtros__busca {
  flex: 1;
}
.notas-filtros__status {
  width: 175px;
}

/* Loading */
.notas-loading {
  padding: 2rem;
  text-align: center;
  color: #6c757d;
}

/* Tabela */
.notas-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.07);
  background: #fff;
}
.notas-table {
  margin: 0;
}
.notas-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6c757d;
  border-top: none;
  white-space: nowrap;
}
.notas-table__chave {
  font-size: 0.78rem;
  font-family: monospace;
  color: #6c757d;
  cursor: default;
}
.notas-table__acoes {
  white-space: nowrap;
}

/* Vazio */
.notas-vazio {
  padding: 4rem 2rem;
  text-align: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.07);
}

/* Modal overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal XML */
.modal-xml {
  background: #fff;
  border-radius: 8px;
  width: 760px;
  max-width: 95vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-xml__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.modal-xml__header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.modal-xml__content {
  flex: 1;
  overflow: auto;
  padding: 1rem 1.25rem;
  font-size: 0.78rem;
  background: #f8f9fa;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
.modal-xml__footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #dee2e6;
  text-align: right;
}

/* Modal confirmação */
.modal-confirm {
  background: #fff;
  border-radius: 8px;
  width: 480px;
  max-width: 95vw;
  padding: 1.75rem;
}
.modal-confirm__title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.modal-confirm__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

/* Indicadores inline de geração de XML */
.notas-xml-spinner,
.notas-xml-erro {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  font-size: 0.75rem;
}

/* Header botão recarregar */
.notas-header__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
